<template>
  <Skeleton :loading="loading" active :avatar="{ shape: 'square' }">
    <div :class="contentClasses" @click="handleClick">
      <div v-if="details" class="item-body">
        <SvgIcon
          icon-class="coc-metting"
          class-name="coope-icon"
          style="width: 40px; height: 40px; flex-shrink: 0; color: #578fff"
        />
        <div class="coope" style="width: 213px; flex-shrink: 0">
          <div style="margin-bottom:8px">
            <div class="coope-users">
              <span class="coope-theme-title">{{ inviteTitle }}</span><span :class="cocStateClasses">{{ cocStatus }}</span>
            </div>
            <div class="coope-desc" style="color: #999b9f">
              {{ inviterName }} {{$t('home.ToInitiate')}}，{{timeRangeStr}}
            </div>
          </div>
          <div class="coope-tags">
            <div class="coope-tag" v-for="tag in tags" :key="tag.id">
              {{ tag.name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Skeleton>
</template>
<script>
import defAvatar from '@/assets/def-avatar.png'
import { Skeleton } from 'ant-design-vue'
import { format } from 'date-fns'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { map, reject } from 'ramda'
import { CONFERENCE_CONFIRM_STATUS, CONFERENCE_STATE, CONFERENCE_STATE_OPTIONS } from '@/utils/constant'

const todayCache = format(new Date(), 'yyyy-MM-dd')

export default {
  name: 'CocListItem',
  props: {
    cocId: {
      type: String,
      required: true
    },
    index: {
      type: Number,
      default: 0
    }
  },
  components: {
    Skeleton
  },
  data() {
    return {
      loading: true,
      error: false,
      defAvatar
    }
  },
  created() {
    this.timer = setTimeout(async () => {
      try {
        await this.fetchCocDetail({ id: this.cocId, force: false })
        this.$nextTick(() => {
          this.loading = false
        })
      } catch (_) {
        // 用于Error处理逻辑，比如失败的界面展示
        this.error = true
      }
      // 每5个1批，进行"并发". 每一批的前5个，等待时间为"0"
    }, Math.floor((this.index % this.limit) / 5) * 500)
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  methods: {
    ...mapActions('coc', ['fetchCocDetail', 'fetchRecordDetail']),
    ...mapMutations('coc', ['setCurrentCocId']),
    handleLoadError(ev) {
      ev.target.src = defAvatar
    },
    async handleClick() {
      const details = this.details
      if (details) {
        const conferenceId = details.conferenceId
        if (this.currentCocId === conferenceId) {
          return
        }
        this.setCurrentCocId(conferenceId)

        const { name, query } = this.$route

        this.$router.replace({
          name,
          query: {
            ...query,
            conferenceId: conferenceId
          }
        })

        if (details.roomId) {
          await this.fetchRecordDetail({ channelId: details.roomId })
        }
      }
    }
  },
  computed: {
    ...mapGetters('coc', ['getCocDetialById', 'limit']),
    ...mapState('coc', ['currentCocId']),
    ...mapState({
      myId: state => state.userInfo.id
    }),
    tags(){
      return this.details.tags || []
    },
    inviterName(){
      const { hostIsMe, hostId, memberByUid } = this.details
      if (hostIsMe) {
        return this.$t('home.Me') // '我'
      } else {
        return memberByUid[hostId].nickName
      }
    },
    inviteTitle(){
      const { members, tags, theme } = this.details
      // 快速协作不翻译
      if (tags && tags.some(tag => tag.name === '快速协作' || tag.name === 'Quick collaboration')) {
        return map(user => user.nickName, reject(member => member.userId === this.myId, members || [])).join(' , ')
      }
      return theme
    },
    details() {
      return this.getCocDetialById(this.cocId) || null
    },
    contentClasses() {
      const activeClass =
        this.cocId === this.currentCocId
          ? 'item-content-active'
          : ''
      return `item-content ${activeClass}`
    },
    cocStatus() {
      const { confirmStatus, conferenceState } = this.details
      if (confirmStatus === CONFERENCE_CONFIRM_STATUS.REJETCED) {
        return this.$t('home.Refused')
      }
      return CONFERENCE_STATE_OPTIONS[conferenceState] || this.$t('home.UnknownState')
    },
    cocStateClasses() {
      const { confirmStatus, conferenceState } = this.details
      if (confirmStatus === CONFERENCE_CONFIRM_STATUS.REJETCED) {
        return `coc-status coc-status-${CONFERENCE_STATE.CANCELED}`
      }
      return `coc-status coc-status-${conferenceState}`
    },
    timeRangeStr() {
      const { beginTime, endTime } = this.details
      const start = format(beginTime * 1000, 'yyyy-MM-dd HH:mm')
      const end = format(endTime * 1000, 'yyyy-MM-dd HH:mm')
      if (start.startsWith(todayCache) && end.startsWith(todayCache)) {
        return `${this.$t('common.Today')}${start.substring(11)}~${end.substring(11)}`
      }

      if (start.substring(0, 11) === end.substring(0, 11)) {
        // 没跨天
        return `${start.substring(11)}~${end.substring(11)}`
      }

      if (start.substring(0, 5) === end.substring(0, 5)) {
        // 没跨年
        return `${start.substring(5)}~${end.substring(5)}`
      }

      // 跨年
      return `${start}~${end}`
    }
  }
}
</script>
<style lang="less" scoped>
@import url(~@/styles/font.less);
@import url('./detail.less');
.item-content {
  padding-left: 4px;
  border-left: 4px solid transparent;
  &::v-deep > .item-body {
    height: 96px;
    display: flex;
    justify-content: space-between;
    padding: 12px 16px 12px 17px;
    margin-top: 4px;
    margin-bottom: 4px;
    border-radius: 8px;
    cursor: pointer;
  }

  &:not(.item-content-active) {
    ::v-deep > .item-body:hover {
      background: #F3F6FB;
    }
  }

  &.item-content-active {
    border-left-color: #236cff;
    background: #ebf1ff;
    .coope-tag {
      background: #fff;
    }
  }
}
.coope-users {
  .medium-font(16);
  color: #333333;
  display: flex;
  align-items: center;
  height: 22px;
  line-height: 22px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-bottom: 4px;
}
.coope-theme-title {
    display: inline-block;
    max-width: 144px;
    text-overflow: ellipsis;
    overflow: hidden;
    vertical-align: text-top;
}
.coope-desc {
  .regular-font(14);
  height: 20px;
  color: #999b9f;
  line-height: 20px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
.coope-tags {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  & > .coope-tag {
    .regular-font(12);
    height: 20px;
    margin-right: 8px;
    &:last-child {
      margin-right: 0;
    }
  }
}
</style>
